import { animate, motion, useMotionValue, useReducedMotionConfig } from "motion/react";
import { useCallback, useLayoutEffect, useRef, type ComponentProps, type FocusEvent, type PointerEvent } from "react";

/**
 * FR-015-002, `contracts/design-system.md` § The shell: a list of chrome rows (the sidebar's rows, the
 * rail's tiles) with one shared highlight that glides to the row under the pointer or the keyboard
 * focus and fades out when neither holds a row, the mechanism of Spectrum UI's Tree Nav (`@spectrumui/tree-nav`,
 * Apache-2.0): a critically damped spring with no overshoot, re-targeted from the live value so a
 * sweep down the list never stutters, every moving value a motion value so a hover re-renders nothing,
 * and a jump in place of the glide when motion is reduced (the system setting or `data-motion`
 * through ThemeProvider's `MotionConfig`).
 *
 * What differs from the source, and why: a row is any element marked `data-glide-row` inside one of
 * the list's items, because a project row carries its toggle and its own nested list; the current row
 * keeps its own raised fill and the indicator bar the owner's measured reference draws, and the shared
 * highlight is the hover tone alone: it never parks on the current row, and on leaving the list it
 * fades where it is rather than travelling back (2026-09-17, by the owner's decision); the
 * source's tree rail, its accent run and its diamond marker are not drawn, because the rail of modes
 * and the indicator bar already say where the reader is; and positions are measured from each row's
 * box, so rows of different heights glide to their own height.
 */
const GLIDE = { type: "spring", visualDuration: 0.22, bounce: 0 } as const;
const FADE = { duration: 0.12, ease: "easeOut" } as const;

export interface GlideListProps extends Omit<ComponentProps<"ul">, "className"> {
  readonly className: string;
  /** The shared highlight's look: its inline extent, radius and fill. */
  readonly pillClassName: string;
}

interface RowBox {
  readonly top: number;
  readonly height: number;
}

function rowsOf(list: HTMLUListElement): HTMLElement[] {
  return [...list.children].flatMap((item) => {
    const row = item.querySelector<HTMLElement>("[data-glide-row]");
    return row === null ? [] : [row];
  });
}

export function GlideList({ className, pillClassName, children, ...rest }: GlideListProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const boxes = useRef<readonly RowBox[]>([]);
  const hovered = useRef<number | null>(null);
  const reduced = useReducedMotionConfig() === true;
  const reducedRef = useRef(reduced);
  useLayoutEffect(() => {
    reducedRef.current = reduced;
  });
  const y = useMotionValue(0);
  const height = useMotionValue(0);
  const opacity = useMotionValue(0);

  const moveTo = useCallback(
    (index: number | null, immediate = false) => {
      const box = index === null || index < 0 ? undefined : boxes.current[index];
      if (box === undefined) {
        if (reducedRef.current) opacity.jump(0);
        else animate(opacity, 0, FADE);
        return;
      }
      // Coming back from hidden, the highlight appears on the row rather than travelling from where it was last parked.
      if (immediate || reducedRef.current || opacity.get() < 0.05) {
        y.jump(box.top);
        height.jump(box.height);
      } else {
        animate(y, box.top, GLIDE);
        animate(height, box.height, GLIDE);
      }
      if (reducedRef.current) opacity.jump(1);
      else animate(opacity, 1, FADE);
    },
    [y, height, opacity],
  );

  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;
    const measure = () => {
      const base = list.getBoundingClientRect().top;
      boxes.current = rowsOf(list).map((row) => {
        const rect = row.getBoundingClientRect();
        return { top: rect.top - base, height: rect.height };
      });
      // A re-measure is a layout change, not motion: the highlight settles at once on the held row, or stays hidden.
      moveTo(hovered.current, true);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => {
      observer.disconnect();
    };
  }, [moveTo]);

  const hold = (target: EventTarget) => {
    const list = listRef.current;
    if (list === null || !(target instanceof Element)) return;
    const row = target.closest<HTMLElement>("[data-glide-row]");
    const index = row === null ? -1 : rowsOf(list).indexOf(row);
    // A row of a nested list is not this list's row: this list's highlight fades.
    const next = index < 0 ? null : index;
    if (next === hovered.current) return;
    hovered.current = next;
    moveTo(next);
  };
  const release = () => {
    hovered.current = null;
    moveTo(null);
  };

  return (
    <ul
      ref={listRef}
      className={`relative ${className}`}
      onPointerOver={(event: PointerEvent<HTMLUListElement>) => {
        hold(event.target);
      }}
      onPointerLeave={release}
      onFocus={(event: FocusEvent<HTMLUListElement>) => {
        hold(event.target);
      }}
      onBlur={(event: FocusEvent<HTMLUListElement>) => {
        if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) release();
      }}
      {...rest}
    >
      <motion.li role="presentation" aria-hidden="true" className={`pointer-events-none absolute top-0 ${pillClassName}`} style={{ y, height, opacity }} />
      {children}
    </ul>
  );
}
